import React from "react";

export default function PredictiveMaintenanceSection() {
  const statusTags = [
    { label: "Healthy", bg: "#d9f2e6", color: "#137a4b" },
    { label: "Maintenance Due", bg: "#fdecd3", color: "#a6620d" },
    { label: "Attention Required", bg: "#fbe1cf", color: "#b04a1a" },
    { label: "Critical", bg: "#fbdcdc", color: "#b3261e" },
  ];

  const featuresLeft = [
    "Maintenance Calendar",
    "Service History",
    "Breakdown Tracking",
    "Service Providers",
  ];
  const featuresRight = [
    "Preventive Maintenance",
    "Maintenance Alerts",
    "Spare Parts Records",
    "Downtime Analysis",
  ];

  const insights = [
    {
      title: "CNC Machine #04",
      desc: "Maintenance recommended in 48 operating hours.",
    },
    {
      title: "3D Printer #12",
      desc: "Preventive maintenance due in 7 days.",
    },
    {
      title: "CNC Turning Centre #02",
      desc: "Service history indicates increased maintenance frequency.",
    },
  ];

  const PulseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f7941d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12h4l2 7 4-14 2 7h8" />
    </svg>
  );

  return (
    <section className="pm-section">
      <style>{`
        .pm-section {
          width: 100%;
          background: linear-gradient(180deg, #f7f9fd 0%, #eef1f8 100%);
          font-family: 'Inter', 'Segoe UI', sans-serif;
          box-sizing: border-box;
          padding: 72px 0;
        }
        .pm-wrap {
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 32px;
          display: grid;
          grid-template-columns: 1fr 0.95fr;
          gap: 64px;
          align-items: center;
          box-sizing: border-box;
        }

        /* LEFT */
        .pm-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 9px 18px;
          border-radius: 999px;
          background: #fdecd3;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          color: #0b1330;
        }
        .pm-eyebrow-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #f7941d;
          flex-shrink: 0;
        }
        .pm-heading {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 800;
          font-size: clamp(1.9rem, 4.2vw, 3rem);
          line-height: 1.16;
          letter-spacing: -0.02em;
          color: #0b1330;
          margin-top: 22px;
        }
        .pm-accent { color: #f7941d; }
        .pm-paragraph {
          margin-top: 22px;
          max-width: 520px;
          font-size: clamp(0.95rem, 2vw, 1.05rem);
          line-height: 1.7;
          color: #5a6280;
        }
        .pm-status-row {
          margin-top: 28px;
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }
        .pm-status-pill {
          padding: 10px 20px;
          border-radius: 999px;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 0.9rem;
          font-weight: 700;
          white-space: nowrap;
        }
        .pm-feature-cols {
          margin-top: 32px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 32px;
          max-width: 560px;
        }
        .pm-feature-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 0;
          font-size: 0.98rem;
          color: #3c4157;
        }

        /* RIGHT */
        .pm-right-col {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }
        .pm-insight-card {
          background: #ffffff;
          border: 1px solid #e7eaf3;
          border-radius: 18px;
          padding: 22px 26px;
          box-shadow: 0 20px 40px -30px rgba(11,19,48,0.15);
          box-sizing: border-box;
        }
        .pm-insight-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .pm-insight-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          color: #8b93ad;
        }
        .pm-schedule-btn {
          background: linear-gradient(135deg, #f7941d, #ef7f0e);
          color: #0b1330;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 0.85rem;
          padding: 8px 18px;
          border-radius: 999px;
          border: none;
          white-space: nowrap;
        }
        .pm-insight-title {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 1.15rem;
          color: #0b1330;
          margin: 10px 0 6px;
        }
        .pm-insight-desc {
          font-size: 0.95rem;
          line-height: 1.55;
          color: #6b7290;
          margin: 0;
        }
        .pm-image-box {
          width: 100%;
          aspect-ratio: 16 / 9;
          border-radius: 18px;
          overflow: hidden;
          position: relative;
          background:
            radial-gradient(circle at 42% 55%, rgba(255,190,110,0.9) 0%, rgba(247,148,29,0.35) 18%, rgba(0,0,0,0) 40%),
            linear-gradient(120deg, #0b1330 0%, #1b2340 55%, #2a3358 100%);
        }
        .pm-image-caption {
          position: absolute;
          bottom: 14px;
          left: 18px;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 0.75rem;
          letter-spacing: 0.08em;
          color: rgba(255,255,255,0.55);
          text-transform: uppercase;
        }

        /* Tablet: stack the two columns */
        @media (max-width: 960px) {
          .pm-wrap { grid-template-columns: 1fr; gap: 40px; }
          .pm-paragraph { max-width: 100%; }
        }

        /* Phones: feature list single column, tighter spacing */
        @media (max-width: 640px) {
          .pm-section { padding: 48px 0; }
          .pm-wrap { padding: 0 20px; }
          .pm-feature-cols { grid-template-columns: 1fr; max-width: 100%; }
          .pm-insight-card { padding: 18px 20px; }
        }

        @media (max-width: 400px) {
          .pm-status-pill { font-size: 0.8rem; padding: 8px 14px; }
          .pm-insight-title { font-size: 1.05rem; }
        }
      `}</style>

      <div className="pm-wrap">
        {/* LEFT */}
        <div>
          <span className="pm-eyebrow">
            <span className="pm-eyebrow-dot" />
            PREDICTIVE MAINTENANCE · OPTIONAL
          </span>

          <h2 className="pm-heading">
            Predict maintenance before <span className="pm-accent">downtime happens.</span>
          </h2>

          <p className="pm-paragraph">
            Unexpected breakdowns delay projects, increase costs and affect
            customer commitments. APIC ProtoHub helps facilities monitor
            usage, maintenance schedules and service history to catch issues
            before they become operational problems.
          </p>

          <div className="pm-status-row">
            {statusTags.map((s, i) => (
              <span
                key={i}
                className="pm-status-pill"
                style={{ background: s.bg, color: s.color }}
              >
                {s.label}
              </span>
            ))}
          </div>

          <div className="pm-feature-cols">
            <div>
              {featuresLeft.map((f, i) => (
                <div className="pm-feature-item" key={i}>
                  <PulseIcon />
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <div>
              {featuresRight.map((f, i) => (
                <div className="pm-feature-item" key={i}>
                  <PulseIcon />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="pm-right-col">
          {insights.map((ins, i) => (
            <div className="pm-insight-card" key={i}>
              <div className="pm-insight-top">
                <span className="pm-insight-label">
                  <PulseIcon />
                  PREDICTIVE INSIGHT
                </span>
                <button className="pm-schedule-btn">Schedule</button>
              </div>
              <h3 className="pm-insight-title">{ins.title}</h3>
              <p className="pm-insight-desc">{ins.desc}</p>
            </div>
          ))}

          {/* Replace this placeholder with your own machining photo */}
          <div className="pm-image-box">
            <span className="pm-image-caption">Add facility photo here</span>
          </div>
        </div>
      </div>
    </section>
  );
}